/**
 * Admin-wide timezone (figures, filters, displays, messaging, etc.).
 * Through October 6, 2026, a figures day closes at 3:00 AM Pacific, so a
 * grade before 3:00 AM still belongs to the previous day. From October 7
 * the day changes at 12:00 AM Pacific.
 */
export const ADMIN_FIGURES_TIMEZONE = "America/Los_Angeles";

/** Alias for non-figures admin call sites. */
export const ADMIN_TIMEZONE = ADMIN_FIGURES_TIMEZONE;

/** User-facing admin timezone label. Pacific Time is shown as PST. */
export const ADMIN_TIMEZONE_LABEL = "PST";

/** Hour (0–23) used before the midnight cutover. 3:00 AM Pacific. */
export const ADMIN_WEEK_START_HOUR = 3;

/** Hour (0–23) from the cutover. 12:00 AM Pacific. */
export const ADMIN_FIGURES_RESET_HOUR = 0;

/**
 * Last Pacific calendar day that still closes at 3:00 AM.
 * Oct 6 12:00 AM–2:59 AM stays on Monday. Oct 7 starts at midnight.
 */
export const ADMIN_FIGURES_MIDNIGHT_RESET_SINCE = { year: 2026, month: 10, day: 6 } as const;

/** 3:00 AM through Oct 6 2026; midnight from Oct 7. Always Pacific. */
export const getAdminFiguresResetHour = (year: number, month: number, day: number): number => {
  const since = ADMIN_FIGURES_MIDNIGHT_RESET_SINCE;
  const usesMidnight =
    year > since.year ||
    (year === since.year && month > since.month) ||
    (year === since.year && month === since.month && day > since.day);
  return usesMidnight ? ADMIN_FIGURES_RESET_HOUR : ADMIN_WEEK_START_HOUR;
};

const WEEKDAY_TO_MONDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export type AdminFiguresZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: string;
};

const getZonedParts = (
  date: Date,
  timeZone = ADMIN_FIGURES_TIMEZONE,
): AdminFiguresZonedParts => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") map[part.type] = part.value;
  }

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    weekday: map.weekday,
  };
};

const getTimeZoneOffsetMs = (date: Date, timeZone = ADMIN_FIGURES_TIMEZONE): number => {
  const parts = getZonedParts(date, timeZone);
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  const truncated = Math.floor(date.getTime() / 1000) * 1000;
  return wallAsUtc - truncated;
};

export const adminWallTimeToUtc = (
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0,
  timeZone = ADMIN_FIGURES_TIMEZONE,
): Date => {
  let utc = Date.UTC(year, month - 1, day, hour, minute, second, 0);
  for (let i = 0; i < 2; i++) {
    const offset = getTimeZoneOffsetMs(new Date(utc), timeZone);
    utc = Date.UTC(year, month - 1, day, hour, minute, second, 0) - offset;
  }
  return new Date(utc + ms);
};

export const getAdminFiguresZonedParts = (
  date: Date,
  timeZone = ADMIN_FIGURES_TIMEZONE,
): AdminFiguresZonedParts => getZonedParts(date, timeZone);

export const getAdminFiguresDayStart = (date: Date): Date => {
  const { year, month, day } = getZonedParts(date);
  return adminWallTimeToUtc(year, month, day, 0, 0, 0, 0);
};

export const addAdminFiguresCalendarDays = (date: Date, days: number): Date => {
  const { year, month, day } = getZonedParts(date);
  const mid = adminWallTimeToUtc(year, month, day, 12, 0, 0, 0);
  mid.setTime(mid.getTime() + days * 24 * 60 * 60 * 1000);
  return getAdminFiguresDayStart(mid);
};

export const getAdminFiguresDayEnd = (date: Date): Date => {
  const nextStart = addAdminFiguresCalendarDays(getAdminFiguresDayStart(date), 1);
  return new Date(nextStart.getTime() - 1);
};

/**
 * Hours before that day's reset roll back to the previous calendar day.
 * Through Oct 6 2026 the reset is 3:00 AM. From Oct 7 it is 12:00 AM Pacific.
 */
export const getAdminFiguresBusinessDayStart = (date: Date): Date => {
  const parts = getZonedParts(date);
  const hour = getAdminFiguresResetHour(parts.year, parts.month, parts.day);
  const dayStart = adminWallTimeToUtc(parts.year, parts.month, parts.day, 0, 0, 0, 0);
  if (parts.hour < hour) {
    const prev = addAdminFiguresCalendarDays(dayStart, -1);
    const prevParts = getZonedParts(prev);
    return adminWallTimeToUtc(
      prevParts.year,
      prevParts.month,
      prevParts.day,
      getAdminFiguresResetHour(prevParts.year, prevParts.month, prevParts.day),
      0,
      0,
      0,
    );
  }
  return adminWallTimeToUtc(parts.year, parts.month, parts.day, hour, 0, 0, 0);
};

/** Monday = 0 … Sunday = 6 using the Pacific figures-day rule. */
export const getAdminFiguresWeekdayIndex = (date: Date): number => {
  const { weekday } = getZonedParts(getAdminFiguresBusinessDayStart(date));
  return WEEKDAY_TO_MONDAY_INDEX[weekday] ?? 0;
};

export const getAdminFiguresDateKey = (date: Date): string => {
  const { year, month, day } = getZonedParts(date);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const getAdminFiguresBusinessDateKey = (date: Date): string =>
  getAdminFiguresDateKey(getAdminFiguresBusinessDayStart(date));

export const isSameAdminFiguresBusinessDay = (a: Date, b: Date): boolean =>
  getAdminFiguresBusinessDateKey(a) === getAdminFiguresBusinessDateKey(b);

export const formatAdminFiguresDateLabel = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string =>
  date.toLocaleDateString("en-US", { ...options, timeZone: ADMIN_FIGURES_TIMEZONE });

export const formatAdminFiguresDateTime = (
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string =>
  date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    ...options,
    // Always win over caller options so the viewer local TZ never shifts admin figures.
    timeZone: ADMIN_FIGURES_TIMEZONE,
  });

/** Short date for admin form labels (always Pacific). */
export const formatAdminFiguresShortDate = (date: Date = new Date()): string =>
  formatAdminFiguresDateLabel(date, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

export const formatAdminFiguresTime = (
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string =>
  date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...options,
    timeZone: ADMIN_FIGURES_TIMEZONE,
  });

/** Short Pacific label for a timestamp: `PDT` or `PST`. */
export const formatAdminFiguresTimeZoneName = (date: Date = new Date()): string => {
  const tzName = new Intl.DateTimeFormat("en-US", {
    timeZone: ADMIN_FIGURES_TIMEZONE,
    timeZoneName: "short",
  })
    .formatToParts(date)
    .find(part => part.type === "timeZoneName")?.value;

  return tzName || "PT";
};

/**
 * Parse an `<input type="date">` value (`YYYY-MM-DD`) as that calendar day
 * in America/Los_Angeles (start 00:00:00.000 or end 23:59:59.999 Pacific).
 */
export const adminDateInputToUtc = (dateString: string, isEnd = false): Date => {
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return new Date(NaN);
  return isEnd
    ? adminWallTimeToUtc(year, month, day, 23, 59, 59, 999)
    : adminWallTimeToUtc(year, month, day, 0, 0, 0, 0);
};

export const isSameAdminFiguresCalendarDay = (a: Date, b: Date): boolean =>
  getAdminFiguresDateKey(a) === getAdminFiguresDateKey(b);

export const getAdminFiguresMonthStart = (date: Date = new Date(), monthsAgo = 0): Date => {
  const parts = getZonedParts(date);
  const totalMonths = parts.year * 12 + (parts.month - 1) - monthsAgo;
  const year = Math.floor(totalMonths / 12);
  const month = (totalMonths % 12) + 1;
  return adminWallTimeToUtc(year, month, 1, 0, 0, 0, 0);
};

export const getAdminFiguresMonthKey = (date: Date): string => {
  const { year, month } = getZonedParts(date);
  return `${year}-${String(month).padStart(2, "0")}`;
};

export const getAdminFiguresYearKey = (date: Date): string =>
  String(getZonedParts(date).year);

export const getAdminFiguresWeekStartFromMonday = (mondayDayStart: Date): Date => {
  const { year, month, day } = getZonedParts(mondayDayStart);
  return adminWallTimeToUtc(year, month, day, getAdminFiguresResetHour(year, month, day), 0, 0, 0);
};

export const shiftAdminFiguresWeekStart = (weekStart: Date, weeks: number): Date => {
  const monday = addAdminFiguresCalendarDays(getAdminFiguresDayStart(weekStart), weeks * 7);
  return getAdminFiguresWeekStartFromMonday(monday);
};

export const getAdminFiguresWeekKey = (date: Date): string => {
  const businessDay = getAdminFiguresBusinessDayStart(date);
  const { weekday } = getZonedParts(businessDay);
  const mondayIndex = WEEKDAY_TO_MONDAY_INDEX[weekday] ?? 0;
  const monday = addAdminFiguresCalendarDays(businessDay, -mondayIndex);
  return getAdminFiguresDateKey(monday);
};

/**
 * ISO week id in Pacific admin TZ: `"YYYY-WW"` (e.g. `"2026-30"`).
 * Uses the Pacific figures-day boundary (3:00 AM, or midnight from Oct 6 2026).
 */
export const getAdminFiguresIsoWeekId = (date: Date): string => {
  const businessDay = getAdminFiguresBusinessDayStart(date);
  const monday = addAdminFiguresCalendarDays(
    businessDay,
    -getAdminFiguresWeekdayIndex(date),
  );
  const thursday = addAdminFiguresCalendarDays(monday, 3);
  const { year: isoYear } = getZonedParts(thursday);
  const jan4 = adminWallTimeToUtc(isoYear, 1, 4, 12, 0, 0, 0);
  const week1Monday = addAdminFiguresCalendarDays(
    getAdminFiguresBusinessDayStart(jan4),
    -getAdminFiguresWeekdayIndex(jan4),
  );
  const weekNum =
    Math.round((monday.getTime() - week1Monday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return `${isoYear}-${String(weekNum).padStart(2, "0")}`;
};
